import { ReferenceManyToManyInput } from "@react-admin/ra-relationships";
import {
  Edit,
  SimpleForm,
  ReferenceInput,
  AutocompleteInput,
  required,
  useTranslate,
  AutocompleteArrayInput,
  useDeleteMany,
  useUpdate,
  Toolbar,
  useEditContext,
  DeleteWithConfirmButton,
  useDelete,
  DateInput
} from "react-admin";
import * as Sentry from "@sentry/react";
import { useParams, useNavigate } from "react-router-dom";
import * as User from "../providers/user";
import * as Axios from "../providers/axios";
import CustomSaveButton from "./CustomSaveButton";
import { useState } from "react";
import AvailabilityDateAndTimes from "./AvailabilityDateAndTimes";
import dayjs from "dayjs";
import { useQuery } from "@apollo/client";
import { GET_LOCATIONS } from "gql";

interface AvailabilityEditProps {
  setRecord?: (record: any) => void;
  editDialogId?: string;
  closeEditDialogAndRefresh?: () => void;
  loading: boolean;
  setLoading: (loading: boolean) => void;
}

interface LocationResult {
  id: string;
  company_id: string;
  timezone: string;
}

function CustomToolbar({
  closeEditDialogAndRefresh,
  loading,
  setRecord
}: AvailabilityEditProps) {
  const { user } = User.useUser();
  const translate = useTranslate();
  const navigate = useNavigate();
  const { record } = useEditContext();
  const [deleteOne] = useDelete();
  const { api } = Axios.useAxios();

  if (setRecord && record) {
    setRecord(record);
  }

  const handleDeleteAndCloseDialog = () => {
    if (loading) return;
    deleteOne(
      "Availability",
      { id: record?.id },
      {
        onSuccess: () =>
          closeEditDialogAndRefresh && closeEditDialogAndRefresh()
      }
    );
  };
  const handleDelete = async () => {
    if (loading) return;

    try {
      await api.delete("/v1/availability", {
        data: {
          groupAvailabilityId: record.group_availability_id,
          start_time: record.start_time,
          userId: user?.impersonate_id || user?.id
        }
      });
    } catch (error) {
      throw error;
    }

    if (closeEditDialogAndRefresh) {
      closeEditDialogAndRefresh();
    } else {
      navigate("/dashboard/Availability");
    }
  };

  return (
    <Toolbar
      sx={{
        display: "flex",
        justifyContent: "space-between",
        "& .MuiButton-containedPrimary": {
          borderRadius: "32px",
          backgroundColor: "rgba(34, 46, 66, 1)",
          fontSize: "14px",
          textTransform: "capitalize",
          boxShadow: "none",
          "&:hover": {
            backgroundColor: "rgba(22, 30, 44, 1)"
          }
        },
        "& .MuiButtonBase-root.Mui-disabled": {
          backgroundColor: "rgba(48, 47, 46, 0.12)"
        },
        "& .ra-delete-button": {
          textTransform: "capitalize"
        }
      }}
    >
      <CustomSaveButton isLoading={loading} />
      <div className="w-full">
        {record.recurring && (
          <DeleteWithConfirmButton
            onClick={handleDelete}
            label={translate("availability.delete_recurrence")}
            disabled={loading}
          />
        )}
        <DeleteWithConfirmButton
          onClick={handleDeleteAndCloseDialog}
          disabled={loading}
        />
      </div>
    </Toolbar>
  );
}

export default function AvailabilityEdit({
  editDialogId,
  closeEditDialogAndRefresh,
  loading,
  setLoading
}: AvailabilityEditProps) {
  const { user, permissions } = User.useUser();
  const translate = useTranslate();
  const [record, setRecord] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteMany] = useDeleteMany();
  const [edit] = useUpdate();
  const navigate = useNavigate();
  const params = useParams();

  const locationsResult = useQuery(GET_LOCATIONS, {
    variables: {
      where: {
        company_id: { equals: permissions?.companyId }
      }
    },
    fetchPolicy: "no-cache"
  });
  const locationsData: LocationResult[] =
    (locationsResult?.data ?? locationsResult?.previousData)?.locations ?? [];

  const submit = (data: any) => {
    setLoading(true);

    const artists = data[
      "@@ra-many-to-many/Availability/ArtistAvailability/Artist"
    ]?.map((artistId: string) => ({
      artist: { id: artistId }
    }));

    const locationId = data.location?.id || record?.location?.id;
    const locationTimezone =
      locationsData.find((loc) => loc.id === locationId)?.timezone || "UTC";

    const startDate = data.start_date || record?.start_time;
    const startTime = data.start_time || record?.start_time;
    const endTime = data.end_time || record?.end_time;

    const startDateTime = dayjs(startDate)
      .tz(locationTimezone)
      .format("YYYY-MM-DD");
    const startTimeString = dayjs(startTime)
      .tz(locationTimezone)
      .format("HH:mm");
    const endTimeString = dayjs(endTime).tz(locationTimezone).format("HH:mm");

    const startDateTimeString = `${startDateTime} ${startTimeString}`;
    const endDateTimeString = `${startDateTime} ${endTimeString}`;

    const start_time = dayjs
      .tz(startDateTimeString, "YYYY-MM-DD HH:mm", locationTimezone)
      .toISOString();
    const end_time = dayjs
      .tz(endDateTimeString, "YYYY-MM-DD HH:mm", locationTimezone)
      .toISOString();

    const retval: any = {
      inserted_by: {
        id: user?.impersonate_id || user?.id
      },
      location: {
        id: String(locationId)
      },
      start_time,
      end_time,
      status: "confirmed",
      artists
    };

    if (data.recurring) {
      retval.recurring = false;
      retval.group_availability = {};
    }

    deleteMany("ArtistAvailability", {
      ids: data.artists?.map((artist: any) => artist.id) || []
    });

    edit(
      "Availability",
      {
        id: editDialogId || params.id,
        data: retval
      },
      {
        onSuccess: () =>
          closeEditDialogAndRefresh
            ? closeEditDialogAndRefresh()
            : navigate("/dashboard/Availability"),
        onSettled: () => setLoading(false),
        onError: (error: any) => {
          Sentry.captureException(error);
          setError(error.message);
          setLoading(false);
        }
      }
    );
  };

  const getTimeOfDayInMilliseconds = (date: any) => {
    const hours = date.hour() * 60 * 60 * 1000;
    const minutes = date.minute() * 60 * 1000;
    return hours + minutes;
  };

  const validateDates = (values: any) => {
    const errors: any = {};
    const artists =
      values["@@ra-many-to-many/Availability/ArtistAvailability/Artist"];
    const start = getTimeOfDayInMilliseconds(
      dayjs(values.start_time).tz(values.location.timezone)
    );
    const end = getTimeOfDayInMilliseconds(
      dayjs(values.end_time).tz(values.location.timezone)
    );
    if (start && end && start >= end) {
      errors.end = "End date must be after start date";
    }
    if (!artists?.length) {
      errors["@@ra-many-to-many/Availability/ArtistAvailability/Artist"] =
        "At least one artist is required";
    }
    if (!values.location?.id) {
      errors["location.id"] = "Location is required";
    }
    return errors;
  };

  const disabled =
    permissions.role.role !== "OWNER" &&
    permissions.role.role !== "MANAGER" &&
    !permissions.isSuperAdmin;

  return (
    <Edit
      // redirect="list"
      id={editDialogId}
      sx={{
        backgroundColor: "#fff",
        "& .RaToolbar-desktopToolbar": {
          backgroundColor: "#fff",
          justifyContent: "end !important",
          flexDirection: "row-reverse",
          gap: "8px",
          minHeight: "40px",
          padding: "0px 16px 16px 16px"
        },
        "& .RaEdit-noActions": {
          marginTop: 0
        }
      }}
    >
      <SimpleForm
        onSubmit={submit}
        toolbar={
          <>
            {error && <p style={{ color: "red" }}>{error}</p>}
            <CustomToolbar
              closeEditDialogAndRefresh={closeEditDialogAndRefresh}
              loading={loading}
              setLoading={setLoading}
              setRecord={setRecord}
            />
          </>
        }
        sx={{ backgroundColor: "#fff", padding: "0px 16px !important" }}
        validate={validateDates}
        reValidateMode="onBlur"
      >
        <AvailabilityDateAndTimes disabled={disabled} />
        <DateInput
          disabled={disabled}
          source="start_date"
          defaultValue={record?.start_time}
          label={translate("availability.date")}
          fullWidth
          validate={[required()]}
          sx={{ display: "none" }}
        />
        <ReferenceManyToManyInput
          reference="Artist"
          through="ArtistAvailability"
          using="availability_id,artist_id"
          filterChoices={{
            user: {
              is: {
                joined_company_id: permissions.companyId
              }
            },
            is_active: {
              equals: true
            }
          }}
        >
          <AutocompleteArrayInput
            disabled={disabled}
            optionText={(value) =>
              `${value.user.first_name} ${value.user.last_name}`
            }
            label={translate("pagenames.artists")}
            fullWidth
            validate={[required()]}
            matchSuggestion={(_value, choices) =>
              choices?.user?.joined_company_id === permissions.companyId
            }
            setFilter={() => {}}
          />
        </ReferenceManyToManyInput>
        <ReferenceInput
          source="location.id"
          reference="Location"
          filter={{
            company_id: permissions.companyId
          }}
        >
          <AutocompleteInput
            disabled={disabled}
            optionText={(value) => value.nickname || value.name}
            label={translate("availability.location")}
            fullWidth
            validate={[required()]}
          />
        </ReferenceInput>
      </SimpleForm>
    </Edit>
  );
}
